import { motion } from 'framer-motion';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

interface AboutPeterProps {
  language: 'de' | 'ru';
}

export default function AboutPeter({ language }: AboutPeterProps) {
  const headerAnim = useScrollAnimation();
  const portraitAnim = useScrollAnimation();
  const journey = useScrollAnimation();
  const philosophy = useScrollAnimation();
  const music = useScrollAnimation();
  const values = useScrollAnimation();

  const content = {
    de: {
      title: 'Über Peter',
      subtitle: '70 Jahre zwischen zwei Welten',
      intro: 'Peter ist mehr als ein Musiker – er ist ein Brückenbauer zwischen Kulturen, ein Poet mit der Seele eines Philosophen und dem Herzen eines Humanisten.',
      journey: 'Die Reise',
      journeyText: 'Geboren in einer Zeit großer Umbrüche, wuchs Peter zwischen deutscher Tradition und russischer Weite auf. Seine Lieder sind das Ergebnis dieser einzigartigen kulturellen Symbiose – sie sprechen die Sprache Goethes und atmen den Geist Puschkins.',
      philosophy: 'Die Philosophie',
      philosophyText: 'Für Peter ist jedes Lied eine Brücke: zwischen Generationen, zwischen Kulturen, zwischen Vergangenheit und Zukunft. Seine Texte schöpfen aus dem reichen Brunnen deutscher Romantik und russischer Tiefe.',
      music: 'Die Musik',
      musicText: '60 Jahre hat Peter komponiert, getextet, gesungen. Seine Werke umfassen heroische Hymnen, philosophische Betrachtungen, Familienlieder und romantische Balladen – alle durchdrungen von literarischer Eleganz.',
      values: 'Werte & Vision',
      valuesText: 'Integrität, Weisheit und kulturelle Verbundenheit prägen Peters Schaffen. Er glaubt an die Kraft der Worte und Melodien, Menschen zu vereinen und Herzen zu berühren.',
      quote: '„In jedem Lied wohnt eine Seele, in jeder Melodie eine Erinnerung, in jedem Vers eine Brücke zwischen Welten."'
    },
    ru: {
      title: 'О Питере',
      subtitle: '70 лет между двумя мирами',
      intro: 'Питер - это больше, чем музыкант – он строитель мостов между культурами, поэт с душой философа и сердцем гуманиста.',
      journey: 'Путешествие',
      journeyText: 'Родившийся во время великих перемен, Питер вырос между немецкой традицией и русским простором. Его песни - результат этого уникального культурного симбиоза – они говорят на языке Гёте и дышат духом Пушкина.',
      philosophy: 'Философия',
      philosophyText: 'Для Питера каждая песня - это мост: между поколениями, между культурами, между прошлым и будущим. Его тексты черпают из богатого источника немецкого романтизма и русской глубины.',
      music: 'Музыка',
      musicText: '60 лет Питер сочиняет, пишет тексты, поет. Его произведения включают героические гимны, философские размышления, семейные песни и романтические баллады – все пронизаны литературной элегантностью.',
      values: 'Ценности и видение',
      valuesText: 'Честность, мудрость и культурная связь определяют творчество Питера. Он верит в силу слов и мелодий объединять людей и трогать сердца.',
      quote: '„В каждой песне живет душа, в каждой мелодии воспоминание, в каждом стихе мост между мирами."'
    }
  };

  const t = content[language];

  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } }
  };

  const fadeInLeft = {
    hidden: { opacity: 0, x: -60 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: 'easeOut' } }
  };

  const fadeInRight = {
    hidden: { opacity: 0, x: 60 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: 'easeOut' } }
  };

  const scaleIn = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.8, ease: 'easeOut' } }
  };

  return (
    <section id="about" className="py-24 bg-gradient-to-b from-[#F5F5DC] to-white">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div 
          ref={headerAnim.ref}
          initial="hidden"
          animate={headerAnim.isVisible ? "visible" : "hidden"}
          variants={fadeInUp}
          className="text-center mb-20"
        >
          <h2 className="font-serif text-6xl md:text-7xl font-bold text-[#1e3a5f] mb-6">
            {t.title}
          </h2>
          <p className="font-serif text-2xl text-[#8b2332] mb-8">
            {t.subtitle}
          </p>
          <div className="h-1 w-32 bg-gradient-to-r from-transparent via-[#C5B358] to-transparent mx-auto mb-8"></div>
          <p className="font-serif text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
            {t.intro}
          </p>
        </motion.div>

        {/* Main Portrait & Quote */}
        <div className="grid md:grid-cols-5 gap-12 mb-24 items-center">
          <motion.div 
            ref={portraitAnim.ref}
            initial="hidden"
            animate={portraitAnim.isVisible ? "visible" : "hidden"}
            variants={fadeInLeft}
            className="md:col-span-2"
          >
            <div className="relative">
              <div className="w-full h-[600px] rounded-lg overflow-hidden shadow-2xl border-4 border-[#C5B358]">
                <img
                  src="https://public.readdy.ai/ai/img_res/aff38175440449086ff33f6cc613a795.jpg"
                  alt="Peter"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-[#8b2332] rounded-full flex items-center justify-center shadow-xl border-4 border-white">
                <i className="ri-music-2-line text-5xl text-[#C5B358]"></i>
              </div>
            </div>
          </motion.div>

          <motion.div 
            initial="hidden"
            animate={portraitAnim.isVisible ? "visible" : "hidden"}
            variants={fadeInRight}
            className="md:col-span-3"
          >
            <div className="bg-[#F5F5DC] rounded-lg p-12 shadow-lg border-2 border-[#C5B358]">
              <div className="mb-8">
                <i className="ri-double-quotes-l text-5xl text-[#8b2332] mb-4"></i>
                <p className="font-serif text-3xl text-[#1e3a5f] italic leading-relaxed mb-4">
                  {t.quote}
                </p>
                <i className="ri-double-quotes-r text-5xl text-[#8b2332] float-right"></i>
              </div>
              <div className="clear-both h-px bg-gradient-to-r from-transparent via-[#C5B358] to-transparent mb-6"></div>
              <p className="font-serif text-xl text-[#8b2332] text-right">
                — Peter
              </p>
            </div>
          </motion.div>
        </div>

        {/* Detailed Sections */}
        <div className="space-y-16">
          {/* The Journey */}
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              ref={journey.ref}
              initial="hidden"
              animate={journey.isVisible ? "visible" : "hidden"}
              variants={fadeInLeft}
            >
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 bg-[#8b2332] rounded-full flex items-center justify-center mr-4">
                  <i className="ri-compass-3-line text-3xl text-white"></i>
                </div>
                <h3 className="font-serif text-4xl font-bold text-[#1e3a5f]">
                  {t.journey}
                </h3>
              </div>
              <p className="font-serif text-lg text-gray-700 leading-relaxed">
                {t.journeyText}
              </p>
            </motion.div>
            <motion.div 
              initial="hidden"
              animate={journey.isVisible ? "visible" : "hidden"}
              variants={scaleIn}
              className="w-full h-80 rounded-lg overflow-hidden shadow-lg"
            >
              <img
                src="https://readdy.ai/api/search-image?query=Nostalgic%20vintage%20photograph%20of%20two%20parallel%20paths%20through%20misty%20forest%20and%20open%20field%2C%20representing%20cultural%20duality%2C%20artistic%20composition%2C%20warm%20sepia%20filter%2C%20contemplative%20mood%2C%20symbolic%20imagery%2C%20simple%20elegant%20background&width=600&height=400&seq=peter-journey-paths&orientation=landscape"
                alt={t.journey}
                className="w-full h-full object-cover object-top"
              />
            </motion.div>
          </div>

          {/* The Philosophy */}
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div 
              ref={philosophy.ref}
              initial="hidden"
              animate={philosophy.isVisible ? "visible" : "hidden"}
              variants={scaleIn}
              className="w-full h-80 rounded-lg overflow-hidden shadow-lg order-2 md:order-1"
            >
              <img
                src="https://readdy.ai/api/search-image?query=Beautiful%20wooden%20bridge%20over%20peaceful%20stream%20with%20golden%20light%20filtering%20through%20trees%2C%20metaphor%20for%20cultural%20connection%2C%20serene%20landscape%2C%20warm%20tones%2C%20philosophical%20atmosphere%2C%20artistic%20photography%2C%20simple%20natural%20background&width=600&height=400&seq=peter-philosophy-bridge&orientation=landscape"
                alt={t.philosophy}
                className="w-full h-full object-cover object-top"
              />
            </motion.div>
            <motion.div 
              initial="hidden"
              animate={philosophy.isVisible ? "visible" : "hidden"}
              variants={fadeInRight}
              className="order-1 md:order-2"
            >
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 bg-[#8b2332] rounded-full flex items-center justify-center mr-4">
                  <i className="ri-lightbulb-line text-3xl text-white"></i>
                </div>
                <h3 className="font-serif text-4xl font-bold text-[#1e3a5f]">
                  {t.philosophy}
                </h3>
              </div>
              <p className="font-serif text-lg text-gray-700 leading-relaxed">
                {t.philosophyText}
              </p>
            </motion.div>
          </div>

          {/* The Music */}
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              ref={music.ref}
              initial="hidden"
              animate={music.isVisible ? "visible" : "hidden"}
              variants={fadeInLeft}
            >
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 bg-[#8b2332] rounded-full flex items-center justify-center mr-4">
                  <i className="ri-music-line text-3xl text-white"></i>
                </div>
                <h3 className="font-serif text-4xl font-bold text-[#1e3a5f]">
                  {t.music}
                </h3>
              </div>
              <p className="font-serif text-lg text-gray-700 leading-relaxed">
                {t.musicText}
              </p>
            </motion.div>
            <motion.div 
              initial="hidden"
              animate={music.isVisible ? "visible" : "hidden"}
              variants={scaleIn}
              className="w-full h-80 rounded-lg overflow-hidden shadow-lg"
            >
              <img
                src="https://readdy.ai/api/search-image?query=Vintage%20acoustic%20guitar%20with%20open%20music%20sheets%20and%20handwritten%20notes%2C%20warm%20candlelight%20ambiance%2C%20nostalgic%20atmosphere%2C%20artistic%20still%20life%2C%20sepia%20tones%2C%20romantic%20mood%2C%20simple%20elegant%20background&width=600&height=400&seq=peter-music-composition&orientation=landscape"
                alt={t.music}
                className="w-full h-full object-cover object-top"
              />
            </motion.div>
          </div>

          {/* Values & Vision */}
          <motion.div 
            ref={values.ref}
            initial="hidden"
            animate={values.isVisible ? "visible" : "hidden"}
            variants={fadeInUp}
            className="bg-gradient-to-r from-[#1e3a5f] to-[#2a4a6f] rounded-lg p-12 text-center shadow-2xl"
          >
            <div className="flex items-center justify-center mb-6">
              <div className="w-20 h-20 bg-[#C5B358] rounded-full flex items-center justify-center">
                <i className="ri-heart-3-line text-4xl text-white"></i>
              </div>
            </div>
            <h3 className="font-serif text-4xl font-bold text-white mb-6">
              {t.values}
            </h3>
            <p className="font-serif text-xl text-white/90 max-w-4xl mx-auto leading-relaxed">
              {t.valuesText}
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
